import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { PresetEditor } from "./components/PresetEditor";
import AddInIds from "@/constants/AddInIds";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import usePresets from "@/hooks/presets/usePresets";
import { Preset } from "@/models/Preset";

export const ManagePresets: React.FC = () => {
  const { data: presets, isLoading, error } = usePresets();
  const removeAddIn = useRemoveAddIn<Preset>(AddInIds.presets, "id");

  const [selectedPreset, setSelectedPreset] = useState<Partial<Preset> | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const handleDeletePreset = async (presetId: string) => {
    try {
      await removeAddIn.mutateAsync({
        delSearch: presetId,
      });
    } catch (error) {
      console.error("Failed to delete preset:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 4, p: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant={"h5"} gutterBottom>
          {"Presets"}
        </Typography>
        <Button
          variant={"contained"}
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedPreset({ description: "", columns: [] });
            setIsAddingNew(true);
          }}
          sx={{ mb: 2 }}
        >
          {"Add New Preset"}
        </Button>
        <List>
          {isLoading ? (
            <Typography>{"Loading presets..."}</Typography>
          ) : error ? (
            <Typography color={"error"}>{"Failed to load presets."}</Typography>
          ) : presets?.length ? (
            presets.map(preset => (
              <ListItem
                key={preset.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #ddd",
                  padding: "10px",
                }}
              >
                <ListItemText
                  primary={preset.description}
                  secondary={`Diagnostics: ${preset.columns.length}`}
                />
                <Box>
                  <Button
                    variant={"contained"}
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setSelectedPreset(preset);
                      setIsAddingNew(false);
                    }}
                    sx={{ mr: 2 }}
                  >
                    {"Edit"}
                  </Button>
                  <Button
                    variant={"outlined"}
                    color={"error"}
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeletePreset(preset.id)}
                  >
                    {"Delete"}
                  </Button>
                </Box>
              </ListItem>
            ))
          ) : (
            <Typography>{"No presets available."}</Typography>
          )}
        </List>
      </Box>

      <Divider orientation="vertical" flexItem />

      <Box sx={{ flex: 2 }}>
        {selectedPreset || isAddingNew ? (
          <PresetEditor
            preset={selectedPreset}
            onClose={() => {
              setSelectedPreset(null);
              setIsAddingNew(false);
            }}
            isNew={isAddingNew}
          />
        ) : (
          <Typography variant={"h6"}>{"Select a preset to edit or add a new preset."}</Typography>
        )}
      </Box>
    </Box>
  );
};
